import * as i0 from '@angular/core';
import { Pipe, EventEmitter, Component, Input, Output, ViewChild, ViewChildren, Directive, HostListener, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@angular/forms';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import * as i3 from '@purespectrum1/ui/button';
import { ButtonModule } from '@purespectrum1/ui/button';
import { debounceTime } from 'rxjs/operators';
import * as i1 from '@angular/cdk/scrolling';
import { ScrollingModule } from '@angular/cdk/scrolling';

class SearchPipe {
    transform(items, searchText, fields) {
        if (!items) {
            return [];
        }
        if (!searchText) {
            return items;
        }
        searchText = searchText.toLowerCase();
        let filteredItems;
        if (fields && fields.length > 0) {
            filteredItems = items.filter((item) => {
                return fields
                    .map((key) => { var _a; return (_a = item[key]) === null || _a === void 0 ? void 0 : _a.toString().toLowerCase().includes(searchText.toString()); })
                    .some((value) => !!value);
            });
        }
        else {
            filteredItems = items.filter((item) => {
                return item.toLowerCase().includes(searchText);
            });
        }
        return filteredItems;
    }
}
SearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SearchPipe, name: "psUiSearch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'psUiSearch',
                }]
        }] });

class SelectDropdownItemComponent {
    constructor() {
        this.clicked = new EventEmitter();
        this.selectNextItem = new EventEmitter();
        this.selectPreviousItem = new EventEmitter();
        this.exitItems = new EventEmitter();
    }
    click() {
        if (this.clickedItem.isNotSelectable) {
            return;
        }
        this.clicked.emit(this.clickedItem);
    }
    keydown(e, _) {
        switch (e.key) {
            case 'Enter':
                this.click();
                break;
            case ' ':
                this.click();
                break;
            case 'ArrowDown':
                e.preventDefault();
                this.selectNextItem.emit();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.selectPreviousItem.emit();
                break;
            case 'Escape':
                this.exitItems.emit();
                break;
        }
    }
    focus() {
        this.item.nativeElement.focus();
    }
}
SelectDropdownItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SelectDropdownItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: SelectDropdownItemComponent, selector: "ps-ui-select-dropdown-item", inputs: { clickedItem: "clickedItem" }, outputs: { clicked: "clicked", selectNextItem: "selectNextItem", selectPreviousItem: "selectPreviousItem", exitItems: "exitItems" }, viewQueries: [{ propertyName: "item", first: true, predicate: ["item"], descendants: true }], ngImport: i0, template: "<li>\n  <span\n    #item\n    tabindex=\"0\"\n    class=\"dropdown-item\"\n    [ngClass]=\"{ 'dropdown-item-selectable': !clickedItem?.isNotSelectable }\"\n    (click)=\"click()\"\n    (keydown)=\"keydown($event, clickedItem)\"\n  >\n    <ng-content></ng-content>\n  </span>\n</li>\n", styles: [":host{display:block}.dropdown-item{white-space:normal}.dropdown-item-selectable{cursor:pointer}\n"], directives: [{ type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-select-dropdown-item', template: "<li>\n  <span\n    #item\n    tabindex=\"0\"\n    class=\"dropdown-item\"\n    [ngClass]=\"{ 'dropdown-item-selectable': !clickedItem?.isNotSelectable }\"\n    (click)=\"click()\"\n    (keydown)=\"keydown($event, clickedItem)\"\n  >\n    <ng-content></ng-content>\n  </span>\n</li>\n", styles: [":host{display:block}.dropdown-item{white-space:normal}.dropdown-item-selectable{cursor:pointer}\n"] }]
        }], propDecorators: { clickedItem: [{
                type: Input
            }], clicked: [{
                type: Output
            }], selectNextItem: [{
                type: Output
            }], selectPreviousItem: [{
                type: Output
            }], exitItems: [{
                type: Output
            }], item: [{
                type: ViewChild,
                args: ['item']
            }] } });

class Identifier {
    constructor(item, key) {
        this.item = item;
        this.key = key;
    }
    value() {
        return this.item[this.key];
    }
}

class After {
    priority() {
        return 1;
    }
}
class Before {
    priority() {
        return -1;
    }
}

class IdentificationPriority {
    constructor(items, key) {
        this.items = items;
        this.key = key;
    }
    sort() {
        return [...this.items].sort((a, b) => {
            if (this.priority(a, b)) {
                return new After().priority();
            }
            return new Before().priority();
        });
    }
    priority(a, b) {
        const idA = new Identifier(a, this.key).value();
        const idB = new Identifier(b, this.key).value();
        return idA > idB;
    }
}

class SelectionPriority {
    constructor(items, selections, key) {
        this.items = items;
        this.selections = selections;
        this.key = key;
    }
    build() {
        this.keys = this.selections.map((item) => new Identifier(item, this.key).value());
    }
    sort() {
        this.build();
        const [selected, unselected] = this.group();
        return [...selected, ...unselected];
    }
    group() {
        const selected = [];
        const unselected = [];
        for (let item of this.items) {
            if (this.selected(item)) {
                selected.push(item);
                continue;
            }
            unselected.push(item);
        }
        return [selected, unselected];
    }
    selected(item) {
        return this.keys.includes(new Identifier(item, this.key).value());
    }
}

class IdentificationSelectionPriority {
    constructor(items, selections, key) {
        this.items = items;
        this.selections = selections;
        this.key = key;
    }
    sort() {
        const sorted = new IdentificationPriority(this.items, this.key).sort();
        return new SelectionPriority(sorted, this.selections, this.key).sort();
    }
}

class Unsorted {
    constructor(items) {
        this.items = items;
    }
    sort() {
        return [...this.items];
    }
}

class SelectDropdownComponent {
    constructor(_renderer, _elementRef) {
        this._renderer = _renderer;
        this._elementRef = _elementRef;
        this._dropdownItems = [];
        this.idField = 'id';
        this.field = 'text';
        this.searchByKeys = [];
        this.labelNone = 'Select';
        this.labelAlignment = 'center';
        this.enableSearch = true;
        this.clearSearch = true;
        this.multi = false;
        this.allSelected = false;
        this.firstSelected = false;
        this.loading = true;
        this.disabled = false;
        this.icon = false;
        this.iconClass = 'fa fa-folder';
        this.newTextInput = false;
        this.newTextInputPlaceHolder = 'New List';
        this.newInputLabel = 'New List';
        this.noItemMessage = '';
        this.sortBySelected = false;
        this.orderById = false;
        this.orderBy = '';
        this.selectedItem = new EventEmitter();
        this.selectedItems = new EventEmitter();
        this.selectedFooterItem = new EventEmitter();
        // Setting this to the default dropdown item height in Chrome
        this.itemHeight = 29;
        this.isOpen = false;
        this.searchText = new FormControl('');
        this.isButtonClicked = false;
        this.newText = false;
        this.inputItem = new FormControl('');
        this._selected = [];
        this.onChange = () => void 0;
    }
    set dropdownItems(value) {
        this._dropdownItems = value;
        this._init();
    }
    get dropdownItems() {
        return this._dropdownItems;
    }
    get textAlignment() {
        return `text-${this.labelAlignment}`;
    }
    get selected() {
        return this._selected;
    }
    set selected(item) {
        this._selected = item;
        if (this.multi) {
            this.selectedItems.emit(this._selected);
            this.onChange(this._selected);
        }
        else {
            this.selectedItem.emit(this._selected[0]);
            this.onChange(this._selected[0]);
        }
    }
    get selectedItemsCount() {
        return this.multi ? this.selected.length : -1;
    }
    ngOnInit() {
        this._init();
        this._listenToOutsideClicks();
        this._resizeDropdownOnSearch();
    }
    ngOnDestroy() {
        if (this._outsideClickListener) {
            this._outsideClickListener();
        }
    }
    _init() {
        if (!(this.dropdownItems instanceof Array)) {
            return;
        }
        // automatic remove loading
        if (this.loading && this.dropdownItems.length) {
            this.loading = false;
        }
        // start with first item selected
        if (this.firstSelected && this.dropdownItems.length) {
            const first = this.dropdownItems[0];
            this.selected = [first];
            return;
        }
        if (this.multi) {
            if (this.allSelected) {
                // start with all items selected
                this.selectAll();
            }
        }
        this.searchByKeys = [...this.searchByKeys, this.field];
        // prevent change detection after checked errors with height resizing
        setTimeout(() => {
            this._resizeDropdownViewport();
        }, 0);
    }
    _listenToOutsideClicks() {
        this._outsideClickListener = this._renderer.listen('document', 'click', (event) => {
            const attrId = event.target.id;
            if (!this._elementRef.nativeElement.contains(event.target) &&
                ![
                    'footer-button-check',
                    'footer-button',
                    'new-input-remove',
                    'new-input-list',
                    'new-input-icon',
                    'new-input-icon-span',
                ].includes(attrId)) {
                {
                    this.close();
                }
            }
        });
    }
    _resizeDropdownOnSearch() {
        this.searchText.valueChanges.pipe(debounceTime(100)).subscribe(() => {
            // Allow the dropdown to rerender the new items the resize it
            setTimeout(() => {
                this._resizeDropdownViewport();
            }, 0);
        });
    }
    startClick() {
        this.isButtonClicked = true;
    }
    endClick() {
        this.isButtonClicked = false;
    }
    toggle() {
        this.isOpen = !this.isOpen;
        // focus on searchBox when dropdown is opened
        if (this.isOpen) {
            this._focusSearchInput();
            this.dropdownItems = this._sortable().sort();
        }
        else {
            this.toggleButton.nativeElement.focus();
        }
    }
    open() {
        this.isOpen = true;
        // focus on searchBox when dropdown is opened
        this._focusSearchInput();
    }
    close() {
        this.isOpen = false;
    }
    closeFocus() {
        this.isOpen = false;
        this.toggleButton.nativeElement.focus();
    }
    writeValue(item) {
        if (item && !Array.isArray(item)) {
            item = [item];
        }
        this._selected = item || [];
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        // do nothing. Needed for ControlValueAccessor interface
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    scrollUp() {
        this._scrollViewport.scrollTo({ top: 0 });
    }
    focusItems(e, isToggleButton, upClose) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (isToggleButton && !this.isOpen) {
                // when arrow down is pressed in closed dropdown
                // open it
                this.open();
            }
            else if (this.items.first) {
                // focus first item
                this.items.first.focus();
            }
        }
        else if (e.key === 'Escape') {
            this.closeFocus();
        }
        else if (e.key === 'ArrowUp') {
            if (isToggleButton || upClose) {
                // close dropdown when press up and toggle button is focused
                this.closeFocus();
                return;
            }
            const activeEl = document.activeElement;
            if (this.searchInput.nativeElement === activeEl ||
                this.items.first.item.nativeElement === activeEl) {
                // focus toggleButton if first element is focused
                this.toggleButton.nativeElement.focus();
            }
        }
    }
    focusNext(i) {
        const nextItem = this.items.get(i + 1);
        if (nextItem) {
            nextItem.focus();
        }
    }
    focusPrevious(i) {
        const previousItem = this.items.get(i - 1);
        if (previousItem) {
            previousItem.focus();
        }
        else if (this.enableSearch) {
            this.searchInput.nativeElement.focus();
        }
        else {
            this.closeFocus();
        }
    }
    findSelectedItemIndex(item) {
        return this.selected.findIndex((selectedItem) => this._getIdFieldValue(selectedItem) === this._getIdFieldValue(item));
    }
    _focusSearchInput() {
        let el = null;
        if (this.enableSearch) {
            el = this.searchInput;
        }
        else if (this.items.first) {
            // focus first item if search isn't enabled
            el = this.items.first.item;
        }
        if (el) {
            // need to wait the dropdown opening to render
            // before focus the element
            setTimeout(() => {
                el === null || el === void 0 ? void 0 : el.nativeElement.focus();
            }, 0);
        }
    }
    _sortable() {
        if (this.orderBy && this.sortBySelected) {
            return new IdentificationSelectionPriority(this._dropdownItems, this.selected, this.orderBy);
        }
        if (this.orderBy) {
            return new IdentificationPriority(this._dropdownItems, this.orderBy);
        }
        if (this.sortBySelected) {
            return new SelectionPriority(this._dropdownItems, this.selected, this.idField);
        }
        return new Unsorted(this._dropdownItems);
    }
    _getIdFieldValue(item) {
        return item[this.idField];
    }
    get selectedItemLabel() {
        var _a;
        return ((_a = this.selected[0]) === null || _a === void 0 ? void 0 : _a[this.field]) || '';
    }
    get allItemsSelected() {
        return this.selectedItemsCount === this.dropdownItems.length;
    }
    getLabel() {
        if (this.loading) {
            return 'Loading...';
        }
        if (this.label) {
            return this.label;
        }
        if (!this.dropdownItems.length) {
            return this.labelNone;
        }
        if (this.multi) {
            if (this.allItemsSelected) {
                return 'All';
            }
            if (!this.selectedItemsCount) {
                return this.labelNone;
            }
            if (this.selectedItemsCount === 1) {
                return this.selectedItemLabel;
            }
            return this.selectedItemsCount + ' selected';
        }
        // single selection dropdown
        return this.selectedItemLabel || this.labelNone;
    }
    emptySearch() {
        this.searchText.setValue('');
        this._focusSearchInput();
    }
    selectAll() {
        if (!this.dropdownItems.length) {
            return;
        }
        this.selected = [...this.dropdownItems];
        this._focusSearchInput();
    }
    selectNone() {
        if (!this.dropdownItems.length) {
            return;
        }
        this.selected = [];
        this._focusSearchInput();
    }
    selectItem(item) {
        if (this.multi) {
            return this._updateMultiSelect(item);
        }
        this.selected = [item];
        this.isOpen = false;
    }
    _updateMultiSelect(item) {
        const idx = this.findSelectedItemIndex(item);
        if (idx > -1) {
            this.selected = [
                ...this.selected.slice(0, idx),
                ...this.selected.slice(idx + 1),
            ];
        }
        else {
            this.selected = [...this.selected, item];
        }
    }
    changeTextBox() {
        this.newText = !this.newText;
    }
    saveFooterItem() {
        if (this.inputItem.value) {
            this.selectedFooterItem.emit(this.inputItem.value);
            this.newText = false;
            this.inputItem.setValue('');
        }
    }
    _resizeDropdownViewport() {
        // Ensure we have to correct height of the dropdown items
        if (this.items.length) {
            this.itemHeight = this.items.first.item.nativeElement.offsetHeight;
        }
        // We get double scrollbars when the item viewport is larger than dropdown max height
        const MAX_HEIGHT = 250;
        const itemLength = this.items.length;
        let totalScrollViewportHeight = itemLength * this.itemHeight;
        if (this.enableSearch) {
            totalScrollViewportHeight +=
                this._searchContainer.nativeElement.offsetHeight;
        }
        if (this.multi) {
            totalScrollViewportHeight +=
                this._multiSelectContainer.nativeElement.offsetHeight;
        }
        if (this.newTextInput) {
            totalScrollViewportHeight +=
                this._newFooterInputContainer.nativeElement.offsetHeight;
        }
        if (this.noItemMessage && !this.dropdownItems.length) {
            totalScrollViewportHeight +=
                this._noItemMsgContainer.nativeElement.offsetHeight;
        }
        // make room for the border
        totalScrollViewportHeight += 2;
        const heightDiff = totalScrollViewportHeight - this.scrollViewportHeight;
        // Ignore updating the container height if it won't change anything
        if (heightDiff === 0 ||
            (totalScrollViewportHeight > MAX_HEIGHT &&
                this.scrollViewportHeight === MAX_HEIGHT)) {
            return;
        }
        // Use setTimeout to ignore change detection after check errors
        setTimeout(() => {
            if (totalScrollViewportHeight > MAX_HEIGHT) {
                this.scrollViewportHeight = MAX_HEIGHT;
            }
            else {
                this.scrollViewportHeight = totalScrollViewportHeight;
            }
        }, 0);
        // Give the container a moment to repaint before updating the virtual scroll viewport
        setTimeout(() => {
            this._scrollViewport.checkViewportSize();
        }, 50);
    }
}
SelectDropdownComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
SelectDropdownComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: SelectDropdownComponent, selector: "ps-ui-select-dropdown", inputs: { dropdownItems: "dropdownItems", idField: "idField", field: "field", searchByKeys: "searchByKeys", label: "label", labelNone: "labelNone", labelAlignment: "labelAlignment", spanField: "spanField", enableSearch: "enableSearch", clearSearch: "clearSearch", multi: "multi", allSelected: "allSelected", firstSelected: "firstSelected", loading: "loading", disabled: "disabled", icon: "icon", iconClass: "iconClass", newTextInput: "newTextInput", newTextInputPlaceHolder: "newTextInputPlaceHolder", newInputLabel: "newInputLabel", noItemMessage: "noItemMessage", sortBySelected: "sortBySelected", orderById: "orderById", orderBy: "orderBy" }, outputs: { selectedItem: "selectedItem", selectedItems: "selectedItems", selectedFooterItem: "selectedFooterItem" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SelectDropdownComponent,
            multi: true,
        },
    ], viewQueries: [{ propertyName: "toggleButton", first: true, predicate: ["toggleButton"], descendants: true }, { propertyName: "searchInput", first: true, predicate: ["searchInput"], descendants: true }, { propertyName: "_searchContainer", first: true, predicate: ["searchContainer"], descendants: true }, { propertyName: "_multiSelectContainer", first: true, predicate: ["multiSelectContainer"], descendants: true }, { propertyName: "_scrollViewport", first: true, predicate: ["scrollViewport"], descendants: true }, { propertyName: "_newFooterInputContainer", first: true, predicate: ["newFooterInputContainer"], descendants: true }, { propertyName: "_noItemMsgContainer", first: true, predicate: ["noItemMsgContainer"], descendants: true }, { propertyName: "items", predicate: SelectDropdownItemComponent, descendants: true }], exportAs: ["psUiSelectDropdown"], ngImport: i0, template: "<button\n  #toggleButton\n  psUiButton\n  class=\"dropdown-button dropdown-toggle\"\n  [ngClass]=\"[(loading && 'loading') || '', textAlignment]\"\n  (click)=\"toggle()\"\n  (keydown)=\"focusItems($event, true)\"\n  (mousedown)=\"startClick()\"\n  (mouseup)=\"endClick()\"\n  (mouseout)=\"endClick()\"\n  [disabled]=\"disabled\"\n  data-e2e-label\n  type=\"button\"\n>\n  {{ getLabel() }}\n  <div class=\"text-primary\" role=\"status\">\n    <span class=\"visually-hidden\">Loading...</span>\n  </div>\n</button>\n<div class=\"dropdown\">\n  <!--\n      this only gets focus when shift+tab from search input\n      so when get back from search we understand the user wants\n      to close the dropdown or focus another element\n    -->\n  <cdk-virtual-scroll-viewport\n    [itemSize]=\"itemHeight\"\n    #scrollViewport\n    [style.height]=\"scrollViewportHeight + 'px'\"\n    class=\"dropdown-menu user_list_dropdown_menu\"\n    [class.show]=\"isOpen\"\n    (focus)=\"closeFocus()\"\n  >\n    <div\n      class=\"input-group input-group-sm\"\n      *ngIf=\"enableSearch\"\n      #searchContainer\n    >\n      <span class=\"input-group-text\">\n        <i class=\"fa fa-search\" aria-hidden=\"true\"></i>\n      </span>\n      <input\n        type=\"text\"\n        class=\"form-control\"\n        placeholder=\"Search\"\n        [formControl]=\"searchText\"\n        (keydown)=\"focusItems($event, false, true)\"\n        (focus)=\"scrollUp()\"\n        #searchInput\n      />\n      <button\n        *ngIf=\"clearSearch\"\n        class=\"btn btn-outline-default dropdown-input-decorators\"\n        type=\"button\"\n        (click)=\"emptySearch()\"\n        (keydown)=\"focusItems($event, false, true)\"\n      >\n        <i class=\"fa fa-times-circle\" aria-hidden=\"true\"></i>\n      </button>\n    </div>\n\n    <div\n      class=\"btn-group btn-group-sm w-100\"\n      role=\"group\"\n      *ngIf=\"multi\"\n      #multiSelectContainer\n    >\n      <button\n        psUiButton\n        [outline]=\"true\"\n        [size]=\"'small'\"\n        class=\"p-0\"\n        [class.active]=\"allItemsSelected\"\n        type=\"button\"\n        (click)=\"selectAll()\"\n      >\n        <i class=\"fa fa-check\" aria-hidden=\"true\"></i>\n        All\n      </button>\n      <button\n        psUiButton\n        [outline]=\"true\"\n        [size]=\"'small'\"\n        class=\"p-0\"\n        [class.active]=\"!selectedItemsCount\"\n        type=\"button\"\n        (click)=\"selectNone()\"\n      >\n        <i class=\"fa fa-times\" aria-hidden=\"true\"></i>\n        None\n      </button>\n    </div>\n    <ng-container *ngIf=\"dropdownItems\">\n      <div\n        *ngIf=\"noItemMessage && !dropdownItems.length\"\n        class=\"new-item-message\"\n        id=\"new-item-message\"\n        #noItemMsgContainer\n      >\n        <span>\n          {{ noItemMessage }}\n        </span>\n      </div>\n      <ps-ui-select-dropdown-item\n        *cdkVirtualFor=\"\n          let item of dropdownItems | psUiSearch: searchText.value:searchByKeys;\n          let i = index\n        \"\n        [clickedItem]=\"item\"\n        (clicked)=\"selectItem($event)\"\n        (selectNextItem)=\"focusNext(i)\"\n        (selectPreviousItem)=\"focusPrevious(i)\"\n        (exitItems)=\"closeFocus()\"\n      >\n        <span id=\"multi-cb-{{ i }}\" *ngIf=\"multi\">\n          <input\n            id=\"item-{{ i }}\"\n            type=\"checkbox\"\n            class=\"form-check-input\"\n            [checked]=\"findSelectedItemIndex(item) > -1\"\n          />\n        </span>\n        <i\n          [class]=\"iconClass\"\n          class=\"folder-icon\"\n          aria-hidden=\"true\"\n          *ngIf=\"icon\"\n        ></i>\n        {{ item[field] }}\n        <span class=\"pull-right\" *ngIf=\"spanField\">\n          {{ item[spanField] }}\n        </span>\n      </ps-ui-select-dropdown-item>\n      <div *ngIf=\"newTextInput\" class=\"new-input-list\" id=\"new-input-list\">\n        <span\n          (click)=\"changeTextBox()\"\n          class=\"dropdown-item dropdown-item-selectable\"\n          *ngIf=\"!newText\"\n          id=\"new-input-icon-span\"\n          #newFooterInputContainer\n        >\n          <img\n            src=\"/assets/image/folder-plus-solid.svg\"\n            class=\"folder-icon-plus\"\n            id=\"new-input-icon\"\n            alt=\"+\"\n            #newinputicon\n          />\n          {{ newInputLabel }}\n        </span>\n        <span\n          *ngIf=\"newText\"\n          class=\"dropdown-item dropdown-item-selectable footer-input-text\"\n          id=\"new-input-list\"\n        >\n          <i\n            class=\"fa fa-times-circle\"\n            aria-hidden=\"true\"\n            (click)=\"changeTextBox()\"\n            id=\"new-input-remove\"\n            #newinputemove\n          ></i\n          ><input\n            type=\"text\"\n            placeholder=\"{{ newTextInputPlaceHolder }}\"\n            [formControl]=\"inputItem\"\n          />\n          <button\n            color=\"secondary\"\n            (click)=\"saveFooterItem()\"\n            id=\"footer-button\"\n            #footerbutton\n          >\n            <i\n              class=\"fa fa-check\"\n              aria-hidden=\"true\"\n              id=\"footer-button-check\"\n              #footerbuttoncheck\n            ></i>\n          </button>\n        </span>\n      </div>\n    </ng-container>\n  </cdk-virtual-scroll-viewport>\n</div>\n", styles: [".pull-right{float:right}.user_list_dropdown_menu{width:100%;height:250px;max-height:250px;overflow-x:hidden;left:-10000px;display:block;visibility:hidden}.show{left:auto;visibility:visible}.user_list_dropdown_menu .btn-group,.user_list_dropdown_menu .input-group{padding:8px}.user_list_dropdown_menu .btn-group{border-bottom:1px solid #d7d7d7;font-size:small}.user_list_dropdown_menu::-webkit-scrollbar{width:8px}.user_list_dropdown_menu::-webkit-scrollbar-track{background-color:#f6f6f6;border-radius:100px}.user_list_dropdown_menu::-webkit-scrollbar-thumb{background-color:#c5c5c5;border-radius:100px}.dropdown-button{width:100%;text-align:center;text-transform:capitalize;background:#fff;border:1px solid #d7d7d7;border-radius:3px;color:#606365!important;font-size:14px;height:auto;position:relative;padding:22px 10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:OpenSans;font-weight:100}.dropdown-button.dropdown-toggle:after{position:absolute;top:50%;right:5%;transform:translateY(-50%)}.dropdown-button .spinner-border{display:none}.dropdown-button.loading .spinner-border{display:inline-block;position:absolute;top:17px;right:5%}.dropdown-button.loading.dropdown-toggle:after{display:none}.user_list_dropdown_menu button.dropdown-input-decorators{border-color:#ced4da;border-left:solid 1px white}button.dropdown-button[disabled]{background:#e0e0e0}.dropdown-menu{font-size:inherit}.new-input-list{cursor:pointer;position:relative;bottom:0px;width:100%;background:#fff}.footer-input-text{display:flex;align-items:center;padding-right:0;background-color:#e9ecef}.footer-input-text input{width:100%;margin-left:10px;border:1px solid #4a91c5}.footer-input-text button{background:#4a91c5;border-color:#4a91c5;color:#fff}.user_list_dropdown_menu i.folder-icon{margin-right:10px}.fa-folder{color:gray}.folder-icon-plus{width:13px;height:14px;margin-right:10px}.user_list_dropdown_menu .new-input-list{background-color:#e9ecef}.new-item-message{padding:25px 0 35px}.new-item-message span{display:block;text-align:center;margin:0 auto;color:#a2a5a6!important}\n"], components: [{ type: i1.CdkVirtualScrollViewport, selector: "cdk-virtual-scroll-viewport", inputs: ["orientation", "appendOnly"], outputs: ["scrolledIndexChange"] }, { type: SelectDropdownItemComponent, selector: "ps-ui-select-dropdown-item", inputs: ["clickedItem"], outputs: ["clicked", "selectNextItem", "selectPreviousItem", "exitItems"] }], directives: [{ type: i3.ButtonDirective, selector: "button[psUiButton]", inputs: ["disabled"] }, { type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i1.CdkFixedSizeVirtualScroll, selector: "cdk-virtual-scroll-viewport[itemSize]", inputs: ["itemSize", "minBufferPx", "maxBufferPx"] }, { type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i5.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i5.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { type: i1.CdkVirtualForOf, selector: "[cdkVirtualFor][cdkVirtualForOf]", inputs: ["cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplate", "cdkVirtualForTemplateCacheSize"] }], pipes: { "psUiSearch": SearchPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-select-dropdown', exportAs: 'psUiSelectDropdown', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SelectDropdownComponent,
                            multi: true,
                        },
                    ], template: "<button\n  #toggleButton\n  psUiButton\n  class=\"dropdown-button dropdown-toggle\"\n  [ngClass]=\"[(loading && 'loading') || '', textAlignment]\"\n  (click)=\"toggle()\"\n  (keydown)=\"focusItems($event, true)\"\n  (mousedown)=\"startClick()\"\n  (mouseup)=\"endClick()\"\n  (mouseout)=\"endClick()\"\n  [disabled]=\"disabled\"\n  data-e2e-label\n  type=\"button\"\n>\n  {{ getLabel() }}\n  <div class=\"text-primary\" role=\"status\">\n    <span class=\"visually-hidden\">Loading...</span>\n  </div>\n</button>\n<div class=\"dropdown\">\n  <!--\n      this only gets focus when shift+tab from search input\n      so when get back from search we understand the user wants\n      to close the dropdown or focus another element\n    -->\n  <cdk-virtual-scroll-viewport\n    [itemSize]=\"itemHeight\"\n    #scrollViewport\n    [style.height]=\"scrollViewportHeight + 'px'\"\n    class=\"dropdown-menu user_list_dropdown_menu\"\n    [class.show]=\"isOpen\"\n    (focus)=\"closeFocus()\"\n  >\n    <div\n      class=\"input-group input-group-sm\"\n      *ngIf=\"enableSearch\"\n      #searchContainer\n    >\n      <span class=\"input-group-text\">\n        <i class=\"fa fa-search\" aria-hidden=\"true\"></i>\n      </span>\n      <input\n        type=\"text\"\n        class=\"form-control\"\n        placeholder=\"Search\"\n        [formControl]=\"searchText\"\n        (keydown)=\"focusItems($event, false, true)\"\n        (focus)=\"scrollUp()\"\n        #searchInput\n      />\n      <button\n        *ngIf=\"clearSearch\"\n        class=\"btn btn-outline-default dropdown-input-decorators\"\n        type=\"button\"\n        (click)=\"emptySearch()\"\n        (keydown)=\"focusItems($event, false, true)\"\n      >\n        <i class=\"fa fa-times-circle\" aria-hidden=\"true\"></i>\n      </button>\n    </div>\n\n    <div\n      class=\"btn-group btn-group-sm w-100\"\n      role=\"group\"\n      *ngIf=\"multi\"\n      #multiSelectContainer\n    >\n      <button\n        psUiButton\n        [outline]=\"true\"\n        [size]=\"'small'\"\n        class=\"p-0\"\n        [class.active]=\"allItemsSelected\"\n        type=\"button\"\n        (click)=\"selectAll()\"\n      >\n        <i class=\"fa fa-check\" aria-hidden=\"true\"></i>\n        All\n      </button>\n      <button\n        psUiButton\n        [outline]=\"true\"\n        [size]=\"'small'\"\n        class=\"p-0\"\n        [class.active]=\"!selectedItemsCount\"\n        type=\"button\"\n        (click)=\"selectNone()\"\n      >\n        <i class=\"fa fa-times\" aria-hidden=\"true\"></i>\n        None\n      </button>\n    </div>\n    <ng-container *ngIf=\"dropdownItems\">\n      <div\n        *ngIf=\"noItemMessage && !dropdownItems.length\"\n        class=\"new-item-message\"\n        id=\"new-item-message\"\n        #noItemMsgContainer\n      >\n        <span>\n          {{ noItemMessage }}\n        </span>\n      </div>\n      <ps-ui-select-dropdown-item\n        *cdkVirtualFor=\"\n          let item of dropdownItems | psUiSearch: searchText.value:searchByKeys;\n          let i = index\n        \"\n        [clickedItem]=\"item\"\n        (clicked)=\"selectItem($event)\"\n        (selectNextItem)=\"focusNext(i)\"\n        (selectPreviousItem)=\"focusPrevious(i)\"\n        (exitItems)=\"closeFocus()\"\n      >\n        <span id=\"multi-cb-{{ i }}\" *ngIf=\"multi\">\n          <input\n            id=\"item-{{ i }}\"\n            type=\"checkbox\"\n            class=\"form-check-input\"\n            [checked]=\"findSelectedItemIndex(item) > -1\"\n          />\n        </span>\n        <i\n          [class]=\"iconClass\"\n          class=\"folder-icon\"\n          aria-hidden=\"true\"\n          *ngIf=\"icon\"\n        ></i>\n        {{ item[field] }}\n        <span class=\"pull-right\" *ngIf=\"spanField\">\n          {{ item[spanField] }}\n        </span>\n      </ps-ui-select-dropdown-item>\n      <div *ngIf=\"newTextInput\" class=\"new-input-list\" id=\"new-input-list\">\n        <span\n          (click)=\"changeTextBox()\"\n          class=\"dropdown-item dropdown-item-selectable\"\n          *ngIf=\"!newText\"\n          id=\"new-input-icon-span\"\n          #newFooterInputContainer\n        >\n          <img\n            src=\"/assets/image/folder-plus-solid.svg\"\n            class=\"folder-icon-plus\"\n            id=\"new-input-icon\"\n            alt=\"+\"\n            #newinputicon\n          />\n          {{ newInputLabel }}\n        </span>\n        <span\n          *ngIf=\"newText\"\n          class=\"dropdown-item dropdown-item-selectable footer-input-text\"\n          id=\"new-input-list\"\n        >\n          <i\n            class=\"fa fa-times-circle\"\n            aria-hidden=\"true\"\n            (click)=\"changeTextBox()\"\n            id=\"new-input-remove\"\n            #newinputemove\n          ></i\n          ><input\n            type=\"text\"\n            placeholder=\"{{ newTextInputPlaceHolder }}\"\n            [formControl]=\"inputItem\"\n          />\n          <button\n            color=\"secondary\"\n            (click)=\"saveFooterItem()\"\n            id=\"footer-button\"\n            #footerbutton\n          >\n            <i\n              class=\"fa fa-check\"\n              aria-hidden=\"true\"\n              id=\"footer-button-check\"\n              #footerbuttoncheck\n            ></i>\n          </button>\n        </span>\n      </div>\n    </ng-container>\n  </cdk-virtual-scroll-viewport>\n</div>\n", styles: [".pull-right{float:right}.user_list_dropdown_menu{width:100%;height:250px;max-height:250px;overflow-x:hidden;left:-10000px;display:block;visibility:hidden}.show{left:auto;visibility:visible}.user_list_dropdown_menu .btn-group,.user_list_dropdown_menu .input-group{padding:8px}.user_list_dropdown_menu .btn-group{border-bottom:1px solid #d7d7d7;font-size:small}.user_list_dropdown_menu::-webkit-scrollbar{width:8px}.user_list_dropdown_menu::-webkit-scrollbar-track{background-color:#f6f6f6;border-radius:100px}.user_list_dropdown_menu::-webkit-scrollbar-thumb{background-color:#c5c5c5;border-radius:100px}.dropdown-button{width:100%;text-align:center;text-transform:capitalize;background:#fff;border:1px solid #d7d7d7;border-radius:3px;color:#606365!important;font-size:14px;height:auto;position:relative;padding:22px 10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:OpenSans;font-weight:100}.dropdown-button.dropdown-toggle:after{position:absolute;top:50%;right:5%;transform:translateY(-50%)}.dropdown-button .spinner-border{display:none}.dropdown-button.loading .spinner-border{display:inline-block;position:absolute;top:17px;right:5%}.dropdown-button.loading.dropdown-toggle:after{display:none}.user_list_dropdown_menu button.dropdown-input-decorators{border-color:#ced4da;border-left:solid 1px white}button.dropdown-button[disabled]{background:#e0e0e0}.dropdown-menu{font-size:inherit}.new-input-list{cursor:pointer;position:relative;bottom:0px;width:100%;background:#fff}.footer-input-text{display:flex;align-items:center;padding-right:0;background-color:#e9ecef}.footer-input-text input{width:100%;margin-left:10px;border:1px solid #4a91c5}.footer-input-text button{background:#4a91c5;border-color:#4a91c5;color:#fff}.user_list_dropdown_menu i.folder-icon{margin-right:10px}.fa-folder{color:gray}.folder-icon-plus{width:13px;height:14px;margin-right:10px}.user_list_dropdown_menu .new-input-list{background-color:#e9ecef}.new-item-message{padding:25px 0 35px}.new-item-message span{display:block;text-align:center;margin:0 auto;color:#a2a5a6!important}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { dropdownItems: [{
                type: Input
            }], idField: [{
                type: Input
            }], field: [{
                type: Input
            }], searchByKeys: [{
                type: Input
            }], label: [{
                type: Input
            }], labelNone: [{
                type: Input
            }], labelAlignment: [{
                type: Input
            }], spanField: [{
                type: Input
            }], enableSearch: [{
                type: Input
            }], clearSearch: [{
                type: Input
            }], multi: [{
                type: Input
            }], allSelected: [{
                type: Input
            }], firstSelected: [{
                type: Input
            }], loading: [{
                type: Input
            }], disabled: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], newTextInput: [{
                type: Input
            }], newTextInputPlaceHolder: [{
                type: Input
            }], newInputLabel: [{
                type: Input
            }], noItemMessage: [{
                type: Input
            }], sortBySelected: [{
                type: Input
            }], orderById: [{
                type: Input
            }], orderBy: [{
                type: Input
            }], selectedItem: [{
                type: Output
            }], selectedItems: [{
                type: Output
            }], selectedFooterItem: [{
                type: Output
            }], toggleButton: [{
                type: ViewChild,
                args: ['toggleButton']
            }], searchInput: [{
                type: ViewChild,
                args: ['searchInput']
            }], items: [{
                type: ViewChildren,
                args: [SelectDropdownItemComponent]
            }], _searchContainer: [{
                type: ViewChild,
                args: ['searchContainer']
            }], _multiSelectContainer: [{
                type: ViewChild,
                args: ['multiSelectContainer']
            }], _scrollViewport: [{
                type: ViewChild,
                args: ['scrollViewport']
            }], _newFooterInputContainer: [{
                type: ViewChild,
                args: ['newFooterInputContainer']
            }], _noItemMsgContainer: [{
                type: ViewChild,
                args: ['noItemMsgContainer']
            }] } });

class SelectDropdownDirective {
    constructor() { }
    toggleDropdown() {
        var _a;
        (_a = this.dropdown) === null || _a === void 0 ? void 0 : _a.toggle();
    }
    onFocus() {
        console.log('focused');
    }
}
SelectDropdownDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SelectDropdownDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: SelectDropdownDirective, selector: "[psUiSelectDropdownToggleFor]", inputs: { dropdown: ["psUiSelectDropdownToggleFor", "dropdown"] }, host: { listeners: { "click": "toggleDropdown()", "focus": "onFocus()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiSelectDropdownToggleFor]',
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { dropdown: [{
                type: Input,
                args: ['psUiSelectDropdownToggleFor']
            }], toggleDropdown: [{
                type: HostListener,
                args: ['click']
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }] } });

class SelectDropdownModule {
}
SelectDropdownModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SelectDropdownModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownModule, declarations: [SearchPipe,
        SelectDropdownComponent,
        SelectDropdownItemComponent,
        SelectDropdownDirective], imports: [CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        ScrollingModule,
        FormsModule], exports: [SearchPipe,
        SelectDropdownComponent,
        SelectDropdownItemComponent,
        SelectDropdownDirective] });
SelectDropdownModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownModule, imports: [[
            CommonModule,
            ReactiveFormsModule,
            ButtonModule,
            ScrollingModule,
            FormsModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SelectDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SearchPipe,
                        SelectDropdownComponent,
                        SelectDropdownItemComponent,
                        SelectDropdownDirective,
                    ],
                    exports: [
                        SearchPipe,
                        SelectDropdownComponent,
                        SelectDropdownItemComponent,
                        SelectDropdownDirective,
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        ButtonModule,
                        ScrollingModule,
                        FormsModule,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SearchPipe, SelectDropdownComponent, SelectDropdownDirective, SelectDropdownItemComponent, SelectDropdownModule };
